import { StatusEnumAccessRequest } from "@/enum/access_request";
import { useLoad } from "@/hook/global-load/hooks/actions";
import { useUploadFileMutation } from "@/services/file";
import { useUpdatedRequestMutation } from "@/services/request-access";
import { validateDocumentFormat } from "@/utils/validateDocument";
import {
  FilePreviewDialog,
  FileThumbnail,
  usePreviewFiles,
} from "components/file-preview";
import { Button } from "components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "components/ui/card";
import { toast } from "components/ui/use-toast";
import { Upload } from "lucide-react";
import React, { useState } from "react";
import { useParams } from "react-router-dom";

export const CardContract = () => {
  const { id_request } = useParams();

  const { files, setLocalFile } = usePreviewFiles(
    "ACCESS_REQUEST",
    id_request?.toString() || "",
  );

  const [selectedFileIndex, setSelectedFileIndex] = useState<number | null>(
    null,
  );

  const [uploadFile, { isLoading: isLoadingUploadFile }] =
    useUploadFileMutation();
  const [updatedRequest, { isLoading: isLoadingUpdateRequest }] =
    useUpdatedRequestMutation();
  const allIsLoading = isLoadingUploadFile || isLoadingUpdateRequest;

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
    position: number,
  ) => {
    const validFiles = validateDocumentFormat(event);
    if (!validFiles) return;
    const file = validFiles[0];
    const url = URL.createObjectURL(file);

    try {
      await uploadFile({
        file,
        entity_name: "ACCESS_REQUEST",
        entity_id: id_request?.toString() || "",
      }).unwrap();
      await updatedRequest({
        comment: "Um novo arquivo foi enviado.",
        id: id_request?.toString() || "",
        status: StatusEnumAccessRequest.PENDING,
      }).unwrap();
      toast({
        variant: "success",
        description: "Arquivo enviado com sucesso.",
      });
    } catch (error) {
      toast({
        variant: "error",
        description: "ops! ocorreu um erro ao enviar arquivo.",
      });
      console.log(error);
    }

    setLocalFile(position, { name: file.name, url });
  };

  const handlePreviewClick = (index: number) => setSelectedFileIndex(index);

  const renderFilePreview = (index: number) => {
    if (files[index]) {
      return (
        <FileThumbnail
          key={index}
          file={files[index]}
          onClick={() => handlePreviewClick(index)}
        />
      );
    }

    return (
      <label className="flex aspect-square w-full cursor-pointer items-center justify-center rounded-md border border-dashed">
        <Upload className="h-6 w-6 text-muted-foreground" />
        <span className="mt-2 text-sm font-medium text-muted-foreground">
          Upload
        </span>
        <input
          type="file"
          accept=".pdf,.doc,.docx"
          className="hidden"
          onChange={(event) => handleFileUpload(event, index)}
        />
      </label>
    );
  };

  const renderRemainingFiles = () => {
    if (files.length > 3) {
      const remaining = files.length - 3;
      return (
        <div
          className="relative flex h-full w-full items-center justify-center rounded-md border pointer"
          onClick={() => handlePreviewClick(3)}
        >
          <span className="text-muted-foreground text-lg font-medium">
            +{remaining}
          </span>
        </div>
      );
    }
    return null;
  };

  const { load } = useLoad();
  load({
    isLoading: allIsLoading,
  });

  return (
    <Card className="overflow-hidden">
      <CardHeader>
        <CardTitle>Termos</CardTitle>
        <CardDescription className="flex  justify-between flex-col gap-4">
          Clique em qualquer área para anexar ou visualizar arquivos.
          <Button
            variant="link"
            className="w-[125px]"
            onClick={() => handlePreviewClick(1)}
          >
            Visualizar Arquivos
          </Button>
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid gap-2">
          {renderFilePreview(0)}
          <div className="grid grid-cols-3 gap-2">
            {renderFilePreview(1)}
            {renderFilePreview(2)}
            {files.length > 3 ? renderRemainingFiles() : renderFilePreview(3)}
          </div>
        </div>
      </CardContent>
      <FilePreviewDialog
        files={files}
        selectedIndex={selectedFileIndex}
        onSelectedIndexChange={setSelectedFileIndex}
      />
    </Card>
  );
};
